import { useRef, useState } from "react";
import { Ruler, Pentagon } from "lucide-react";
import { toast } from "sonner";

export const fmtMm = (mm) => `${Math.round(mm).toLocaleString("de-DE")} mm`;
export const fmtM2 = (m2) => `${m2.toLocaleString("de-DE", { maximumFractionDigits: 2 })} m²`;
const CLOSE_PX = 16;

// Maßlinie: Linie mit Endstrichen + Maß-Beschriftung entlang der Linie (immer lesbar).
export function drawDimension(ctx, a, b, label, color, zoom = 1) {
  const ang = Math.atan2(b.y - a.y, b.x - a.x);
  const tick = 9 / zoom;
  const nx = -Math.sin(ang) * tick;
  const ny = Math.cos(ang) * tick;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2 / zoom;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
  for (const p of [a, b]) { ctx.moveTo(p.x - nx, p.y - ny); ctx.lineTo(p.x + nx, p.y + ny); }
  ctx.stroke();
  if (label) {
    ctx.translate((a.x + b.x) / 2, (a.y + b.y) / 2);
    ctx.rotate(ang > Math.PI / 2 || ang < -Math.PI / 2 ? ang + Math.PI : ang);
    drawLabel(ctx, label, color, zoom, -(14 / zoom + 8 / zoom) / 2 - 4 / zoom);
  }
  ctx.restore();
}

function drawLabel(ctx, label, color, zoom, dy = 0) {
  const fs = 14 / zoom;
  ctx.font = `700 ${fs}px system-ui, -apple-system, sans-serif`;
  const w = ctx.measureText(label).width + 10 / zoom;
  const h = fs + 8 / zoom;
  ctx.fillStyle = "rgba(9,9,11,0.85)";
  ctx.fillRect(-w / 2, dy - h / 2, w, h);
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label, 0, dy);
}

// Fläche: Polygon mit leichter Füllung, Eckpunkten und m²-Beschriftung in der Mitte.
export function drawArea(ctx, pts, label, color, zoom = 1, closed = true) {
  if (!pts.length) return;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2 / zoom;
  ctx.beginPath();
  pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
  if (closed) {
    ctx.closePath();
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = color;
    ctx.fill();
    ctx.globalAlpha = 1;
  }
  ctx.stroke();
  ctx.fillStyle = color;
  pts.forEach((p) => { ctx.beginPath(); ctx.arc(p.x, p.y, 3.5 / zoom, 0, Math.PI * 2); ctx.fill(); });
  if (label) {
    const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
    ctx.translate(cx, cy);
    drawLabel(ctx, label, color, zoom);
  }
  ctx.restore();
}

const polyAreaPx = (pts) =>
  Math.abs(pts.reduce((s, p, i) => { const q = pts[(i + 1) % pts.length]; return s + p.x * q.y - q.x * p.y; }, 0)) / 2;

/**
 * Lineal + Fläche. tool: null | "line" | "area".
 * Linie: ziehen → mm eintragen (erste = Referenz, danach Auto-Vorschlag).
 * Fläche: Eckpunkte antippen, auf ersten Punkt tippen oder „Fertig“ → m² (braucht Referenz).
 * onStart: vor jeder Messung (z. B. Undo-Snapshot), onCancel: Messung verworfen.
 */
export function useRuler({ ctxRef, canvasRef, color, zoom, onStart, onCancel, historyLen }) {
  const [tool, setToolState] = useState(null);
  const [scale, setScale] = useState(null); // mm pro Canvas-Pixel
  const [pending, setPending] = useState(null);
  const [areaPts, setAreaPts] = useState([]);
  const [transfer, setTransfer] = useState(null); // {kind: "length"|"area", value}
  const [measurements, setMeasurements] = useState([]);
  const drag = useRef(null);
  const snap = useRef(null);
  const lastTouch = useRef(0);

  const capture = () => {
    const c = canvasRef.current;
    snap.current = ctxRef.current.getImageData(0, 0, c.width, c.height);
    onStart?.();
  };
  const restore = () => snap.current && ctxRef.current.putImageData(snap.current, 0, 0);
  const abortArea = () => { if (areaPts.length) { restore(); onCancel?.(); } setAreaPts([]); snap.current = null; };

  const setTool = (t) => {
    abortArea();
    if (t === "area" && !scale) toast.info("Erst mit dem Lineal ein Maß als Referenz setzen – dann wird die Fläche automatisch berechnet.");
    setToolState(t);
  };

  const closeArea = (pts) => {
    if (pts.length < 3) { toast.info("Mindestens 3 Eckpunkte setzen"); return; }
    const m2 = Math.round((polyAreaPx(pts) * scale * scale) / 1e4) / 100;
    restore();
    drawArea(ctxRef.current, pts, fmtM2(m2), color, zoom);
    setAreaPts([]);
    setPending({ kind: "area", value: m2 });
  };

  // Pointer-Down im Mess-Modus
  const down = (p, e) => {
    // Nach einem Tipp feuern Browser zusätzlich Maus-Events → ignorieren
    if (e?.type?.startsWith("touch")) lastTouch.current = Date.now();
    else if (Date.now() - lastTouch.current < 800) return;
    if (tool === "line") {
      capture();
      drag.current = { a: p, b: p };
      return;
    }
    if (!scale) { toast.info("Erst mit dem Lineal ein Maß als Referenz setzen."); return; }
    const pts = areaPts;
    if (pts.length >= 3 && Math.hypot(p.x - pts[0].x, p.y - pts[0].y) < CLOSE_PX / zoom) { closeArea(pts); return; }
    if (!pts.length) capture();
    const next = [...pts, p];
    restore();
    drawArea(ctxRef.current, next, "", color, zoom, false);
    setAreaPts(next);
  };

  const update = (p) => {
    if (!drag.current) return false;
    drag.current.b = p;
    restore();
    const px = Math.hypot(p.x - drag.current.a.x, p.y - drag.current.a.y);
    drawDimension(ctxRef.current, drag.current.a, p, scale ? fmtMm(px * scale) : "", color, zoom);
    return true;
  };
  const finish = () => {
    const d = drag.current;
    if (!d) return false;
    const px = Math.hypot(d.b.x - d.a.x, d.b.y - d.a.y);
    if (px < 8) { cancel(); return true; }
    setPending({ px, suggest: scale ? Math.round(px * scale) : "" });
    return true;
  };
  const confirm = (mm, asRef, name = "") => {
    name = name.trim().slice(0, 60);
    if (pending.kind === "area") {
      snap.current = null;
      setPending(null);
      setTransfer({ kind: "area", value: pending.value });
      setMeasurements((ms) => [...ms, { kind: "area", value: pending.value, name, h: historyLen?.() }]);
      return;
    }
    const d = drag.current;
    restore();
    drawDimension(ctxRef.current, d.a, d.b, fmtMm(mm), color, zoom);
    if (asRef || !scale) setScale(mm / pending.px);
    drag.current = null;
    snap.current = null;
    setPending(null);
    setTransfer({ kind: "length", value: mm });
    setMeasurements((ms) => [...ms, { kind: "length", value: mm, name, h: historyLen?.() }]);
  };
  const cancel = () => { restore(); drag.current = null; snap.current = null; setPending(null); onCancel?.(); };

  // Referenz geräteunabhängig speichern: mm pro gesamter Canvas-Breite
  const refPerWidth = () => (scale ? scale * canvasRef.current.clientWidth : null);
  const loadRef = (perWidth) => { if (perWidth > 0 && canvasRef.current) setScale(perWidth / canvasRef.current.clientWidth); };

  // Nach Rückgängig/Löschen: Messungen entfernen, deren Undo-Schritt nicht mehr existiert
  const trimTo = (len) => setMeasurements((ms) => ms.filter((m) => m.h === undefined ? len > 0 : m.h <= len));

  return {
    measurements: measurements.map(({ kind, value, name }) => ({ kind, value, name })), refPerWidth, loadRef, trimTo,
    tool, setTool, active: !!tool, scale, setScale, pending, areaPts, transfer, setTransfer,
    dragging: () => !!drag.current, down, update, finish, confirm, cancel,
    finishArea: () => closeArea(areaPts), abortArea,
  };
}

const toolBtn = (on) =>
  `h-10 px-2.5 rounded-xl flex items-center gap-1 text-[11px] font-bold ${on ? "bg-[#00E575]/15 ring-1 ring-[#00E575] text-[#00E575]" : "bg-zinc-800 text-zinc-200"}`;

export function RulerButton({ ruler, testid }) {
  return (
    <>
      <button type="button" onClick={() => ruler.setTool(ruler.tool === "line" ? null : "line")} data-testid={testid}
              title="Lineal: Maß einzeichnen" className={toolBtn(ruler.tool === "line")}>
        <Ruler size={18} />
        {ruler.scale ? <span data-testid={`${testid}-ref`}>REF</span> : null}
      </button>
      <button type="button" onClick={() => ruler.setTool(ruler.tool === "area" ? null : "area")} data-testid={`${testid}-area`}
              title="Fläche messen" className={toolBtn(ruler.tool === "area")}>
        <Pentagon size={18} />
      </button>
    </>
  );
}

export function MeasureHint({ ruler, testid }) {
  if (!ruler.tool) return null;
  const text = ruler.tool === "line"
    ? `Lineal: Linie ziehen → Maß in mm eintragen${ruler.scale ? " · misst automatisch" : ""}`
    : ruler.scale ? "Fläche: Eckpunkte antippen, zum Schließen auf den ersten Punkt tippen" : "Fläche: zuerst mit dem Lineal eine Referenz setzen";
  return (
    <div className="absolute top-2 left-2 z-20 flex items-center gap-1.5" data-testid={testid}>
      <div className="rounded-lg bg-black/60 px-2.5 py-1.5 text-[11.5px] text-zinc-100 max-w-[60vw]">{text}</div>
      {ruler.tool === "area" && ruler.areaPts.length >= 3 && (
        <button type="button" onClick={ruler.finishArea} data-testid={`${testid}-area-done`}
                className="h-8 px-3 rounded-lg bg-[#00E575] text-[#09090B] text-[12px] font-bold">Fertig</button>
      )}
      {ruler.tool === "area" && ruler.areaPts.length > 0 && (
        <button type="button" onClick={ruler.abortArea} data-testid={`${testid}-area-abort`}
                className="h-8 px-3 rounded-lg bg-zinc-800 text-zinc-200 text-[12px] font-semibold">Verwerfen</button>
      )}
    </div>
  );
}

const NAME_SUGGEST = {
  length: ["Wandlänge", "Raumbreite", "Raumhöhe", "Türbreite", "Fensterbreite", "Abstand Außengerät", "Leitungslänge"],
  area: ["Heizraum", "Aufstellfläche", "Wohnraum", "Keller", "Dachfläche"],
};

function NameField({ kind, name, setName, used }) {
  const chips = [...new Set([...used, ...NAME_SUGGEST[kind]])].slice(0, 9);
  return (
    <div className="space-y-2">
      <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
             data-testid="ruler-name-input" placeholder="Bezeichnung (optional), z. B. Wand Küche"
             className="w-full h-11 rounded-xl bg-zinc-900 border border-zinc-700 px-3 text-[14px] text-zinc-50 focus:outline-none focus:border-[#00E575]" />
      <div className="flex flex-wrap gap-1.5" data-testid="ruler-name-suggestions">
        {chips.map((c) => (
          <button key={c} type="button" onClick={() => setName(c)} data-testid={`ruler-name-chip-${c}`}
                  className={`h-7 px-2.5 rounded-full text-[11.5px] font-semibold border ${name === c ? "bg-[#00E575]/15 border-[#00E575] text-[#00E575]" : "bg-zinc-900 border-zinc-700 text-zinc-300"}`}>
            {c}
          </button>
        ))}
      </div>
    </div>
  );
}

export function DimensionDialog({ ruler }) {
  const p = ruler.pending;
  const [mm, setMm] = useState("");
  const [asRef, setAsRef] = useState(false);
  const [name, setName] = useState("");
  if (!p) return null;
  const isArea = p.kind === "area";
  const value = isArea ? p.value : mm === "" ? String(p.suggest) : mm;
  const ok = Number(value) > 0;
  const close = () => { setMm(""); setAsRef(false); setName(""); };
  const used = ruler.measurements.filter((m) => m.name && m.kind === (isArea ? "area" : "length")).map((m) => m.name).reverse();
  return (
    <div className="fixed inset-0 z-[95] bg-black/60 flex items-end sm:items-center justify-center p-3" data-testid="ruler-dialog">
      <div className="w-full max-w-sm bg-[#18181B] border border-zinc-700 rounded-2xl p-4 space-y-3">
        <div className="text-[15px] font-bold text-zinc-50">{isArea ? "Fläche benennen" : "Maß eintragen"}</div>
        {isArea
          ? <div className="text-[22px] font-mono font-bold text-[#00E575]" data-testid="ruler-area-value">{fmtM2(p.value)}</div>
          : ruler.scale
            ? <div className="text-[12px] text-zinc-400">Automatisch gemessen anhand der Referenz – bei Bedarf korrigieren.</div>
            : <div className="text-[12px] text-zinc-400">Erstes Maß = Referenz. Danach misst die App jede weitere Linie automatisch.</div>}
        {!isArea && (
          <div className="flex items-center gap-2">
            <input autoFocus type="number" inputMode="numeric" value={value} onChange={(e) => setMm(e.target.value)}
                   data-testid="ruler-mm-input" placeholder="z. B. 2450"
                   className="flex-1 h-12 rounded-xl bg-zinc-900 border border-zinc-700 px-3 text-[18px] font-mono text-zinc-50 focus:outline-none focus:border-[#00E575]" />
            <span className="text-zinc-300 font-bold">mm</span>
          </div>
        )}
        <NameField kind={isArea ? "area" : "length"} name={name} setName={setName} used={used} />
        {!isArea && ruler.scale && (
          <label className="flex items-center gap-2 text-[12.5px] text-zinc-300">
            <input type="checkbox" checked={asRef} onChange={(e) => setAsRef(e.target.checked)} data-testid="ruler-as-ref" />
            Als neue Referenz verwenden
          </label>
        )}
        <div className="flex gap-2">
          <button type="button" disabled={!ok} data-testid="ruler-confirm"
                  onClick={() => { ruler.confirm(Number(value), asRef, name); close(); }}
                  className="h-11 flex-1 rounded-xl bg-[#00E575] text-[#09090B] font-bold disabled:opacity-40">Übernehmen</button>
          <button type="button" data-testid="ruler-cancel" onClick={() => { ruler.cancel(); close(); }}
                  className="h-11 px-4 rounded-xl bg-zinc-800 text-zinc-200 font-semibold">{isArea ? "Verwerfen" : "Abbrechen"}</button>
        </div>
        {!isArea && ruler.scale && (
          <button type="button" data-testid="ruler-reset-ref" onClick={() => ruler.setScale(null)}
                  className="text-[11.5px] text-zinc-500 hover:text-zinc-300 underline">Referenz zurücksetzen</button>
        )}
      </div>
    </div>
  );
}
